import { useState, useEffect } from 'react';
import { AxiosResponse } from 'axios';
import { apiAuthCaller } from '@services/apiCaller';
import useStores from '@stores/useStores';
import { isValidationLoadingType } from '../../addSource';

interface IStep {
  id: number;
  name: string;
  error: string;
  success: boolean;
}

interface IError {
  response:
    | {
        status: number;
      }
    | undefined;
}
export const useSchemaValidation = (
  checkValid: (val: isValidationLoadingType) => void,
  validationStatus: isValidationLoadingType,
  sName: string,
  accountId?: string,
  sourceAccountInfo?: { options?: Record<string, unknown>; secret?: Record<string, unknown> },
) => {
  const [status, setStatus] = useState<isValidationLoadingType>(isValidationLoadingType.hide);
  const [checksInValidation, setChecksInValidation] = useState<IStep[]>([]);
  const [stepStatus, setStepStatus] = useState<isValidationLoadingType[]>([]);
  const { userStore, messagesStore } = useStores();
  // making api call to steps
  const token = userStore.token || '';
  const url = `cloudSources/validation/${sName}/steps`;
  const accountInfo = { ...sourceAccountInfo?.options, ...sourceAccountInfo?.secret };
  const params = { credentials: accountInfo, accountId };

  function syncNextAndStatus(arg: isValidationLoadingType) {
    setStatus(arg);
    checkValid(arg);
  }

  function updateSteps(steps: IStep[]) {
    setChecksInValidation(steps); // initial call and loading the names of validation checks.
    // setting the status of each validation check as listing.
    setStepStatus(new Array(steps.length).fill(isValidationLoadingType.list));
  }

  // updating the validation list
  function updateStepStatus(step: IStep[], stepId: number) {
    setChecksInValidation(
      [...checksInValidation].map((el, i) => (i === stepId - 1 ? step[0] : el)),
    );
    if (step[0].success) {
      setStepStatus(
        [...stepStatus].map((el, i) => (i === stepId - 1 ? isValidationLoadingType.passed : el)),
      );
    } else {
      setStepStatus(
        [...stepStatus].map((el, i) => (i === stepId - 1 ? isValidationLoadingType.failed : el)),
      );
    }
  }

  const getValidationSteps = async () => {
    try {
      const response: AxiosResponse<{ steps: IStep[] }> = await apiAuthCaller(token).post(
        url,
        params,
      );
      if (response.data.steps) {
        updateSteps(response.data.steps);
      } else {
        syncNextAndStatus(isValidationLoadingType.failed);
      }
    } catch (error: unknown) {
      handleError(error);
    }
  };

  const getValidationStepStatus = async (stepId: number) => {
    const urlStep = `${url.replace('steps', 'validate')}?step=${stepId}`;
    try {
      const response: AxiosResponse<{ steps: IStep[] }> = await apiAuthCaller(token).post(
        urlStep,
        params,
      );
      if (response.data.steps) {
        updateStepStatus(response.data.steps, stepId);
      } else {
        syncNextAndStatus(isValidationLoadingType.failed);
      }
    } catch (error: unknown) {
      handleError(error);
    }
  };

  function handleError(error: unknown) {
    let msg;
    if ((error as IError).response?.status === 500) {
      msg = 'Failed to Validate Credentials due to Internal Server Error';
    } else {
      msg = 'Failed to Validate Credentials';
    }
    messagesStore.showErrorMessage(msg);
    syncNextAndStatus(isValidationLoadingType.hide);
  }

  useEffect(() => {
    if (validationStatus === isValidationLoadingType.loading) {
      // function is called only if check is loading.
      getValidationSteps();
    }
    // when the status is changed either from outside (due to validation ) or from inside
    //  we are clearing out validation.
    if (validationStatus !== status && validationStatus !== isValidationLoadingType.notStarted) {
      setChecksInValidation([]);
      setStepStatus([]);
      setStatus(validationStatus);
    }
  }, [validationStatus]);

  // Loads the step call
  // Also deduce the status based on stepstatus, to pass or fail overall, after the loading.
  useEffect(() => {
    stepStatus.forEach((val, i, el) => {
      // first call.
      if (i === 0 && val === isValidationLoadingType.list) {
        loadValidationSteps(1);
      } else if (
        // go ahead only if the prvious call is succeeded.
        val === isValidationLoadingType.list &&
        el[i - 1] === isValidationLoadingType.passed
      ) {
        loadValidationSteps(i + 1);
      }
    });
    if (stepStatus.length > 0 && stepStatus.every((el) => el === isValidationLoadingType.passed)) {
      syncNextAndStatus(isValidationLoadingType.passed);
    } else if (stepStatus.includes(isValidationLoadingType.failed)) {
      syncNextAndStatus(isValidationLoadingType.failed);
    }
  }, [stepStatus]);

  function loadValidationSteps(index: number) {
    // handle step validation  call.
    setStepStatus(
      [...stepStatus].map((el, i) => (i === index - 1 ? isValidationLoadingType.loading : el)),
    );
    getValidationStepStatus(index);
  }

  return {
    validationStatus: status,
    checksInValidation,
    stepStatus,
  };
};
